/* ═══════════════════════════════════════════════════════
   NYC Suite: Holds engine (pure, no React, no Firestore)

   A hold is an open quote's dated sessions. This module is the one contract for:
   - which quotes hold (lifecycle), and on which dates (session rules),
   - crew identity (roster ids) and equipment identity (canonical ids),
   - the hold record every role reads (deriveHoldRecord; slice 2 ports it to the
     Cloud Function trigger, so keep it dependency-light). Since 2026-09-05 the
     record carries the client name and the subject like a booking does, so a
     hold reads the same to admins, crew, and Google Calendar. Money, notes,
     and phone numbers never enter it.
   - conflict detection between holds and booked jobs, and the sentences the
     builder and the saved-quotes panel print for them.

   Every function is pure. The live equipment map (src/lib/equipment.js,
   getEqNames()) and the live roster (useData().crewRoster) are passed in by the
   caller; nothing here reads Firestore. Plan: docs/holds/holds-plan.md.
   ═══════════════════════════════════════════════════════ */

import { STUDIO_ADDRESS } from "./constants.js";
import { FMT_DATE_SHORT, fmtDate, isISODateOnly, normalizeCrew, quoteEquipmentList } from "./utils.js";

/* ═══ Constants ═══ */

// Retired equipment ids that still appear on old records. Each is one physical
// unit with a canonical id; the live map carries these keys too (as display-name
// aliases), so they are canonicalized here before any capacity math.
export const RETIRED_EQUIPMENT_ALIASES = Object.freeze({ mobile: "big_rig", leo_b: "leo", r5e: "r5" });

// Legacy display strings that no longer match a live equipment name.
const LEGACY_EQUIPMENT_NAMES = Object.freeze({
  "portalcam": "portal",
  "mobile big rig": "big_rig",
});

export const UNKNOWN_EQUIPMENT_PREFIX = "unknown:";

const SESSION_KINDS = ["flight", "setup", "capture", "strike"];
const KIND_ORDER = Object.fromEntries(SESSION_KINDS.map((k, i) => [k, i]));

export const STALE_HOLD_DAYS = 30;
const DAY_MS = 86400000;

/* ═══ Small helpers ═══ */

const clean = (v) => String(v == null ? "" : v).trim();
const lower = (v) => clean(v).toLowerCase();

export const slugify = (v) => lower(v).replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

// Strict calendar-date check: the ISO pattern AND a real day (2026-02-30 is out).
// UTC arithmetic, so no local time zone or DST rule can shift a date-only value.
export const isValidHoldDate = (v) => {
  if (!isISODateOnly(v)) return false;
  const [y, m, d] = String(v).split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
};

// Three-way string compare (0 on ties) for every sort in this module.
const cmp = (a, b) => (a < b ? -1 : a > b ? 1 : 0);

// Sorted keys at every level, so the same value always serializes the same.
// Mirrored in functions/src/services/holdRecord.ts (the fingerprint contract).
export function canonicalJson(value) {
  // Same shape JSON.stringify gives: undefined array slots become null and
  // undefined-valued keys are omitted, so the output always parses back.
  if (value === undefined) return "null";
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(",")}]`;
  if (value instanceof Date) return JSON.stringify(value.toISOString());
  if (value && typeof value === "object") {
    return `{${Object.keys(value).sort().filter((k) => value[k] !== undefined).map((k) => `${JSON.stringify(k)}:${canonicalJson(value[k])}`).join(",")}}`;
  }
  return JSON.stringify(value);
}

const shiftDay = (iso, n) => {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d + n);
  const p = (x) => String(x).padStart(2, "0");
  return `${dt.getFullYear()}-${p(dt.getMonth() + 1)}-${p(dt.getDate())}`;
};

const fmtShort = (iso) => fmtDate(iso, FMT_DATE_SHORT);

// "partial" is the only value that means partial; everything else is a full day.
export const normalizeDayType = (v) => (lower(v) === "partial" ? "partial" : "full");

// Unknown or missing session types count as capture (the default session type).
const sessionKind = (v) => (SESSION_KINDS.includes(lower(v)) ? lower(v) : "capture");

// Setup, capture, and strike reserve the equipment; a flight day reserves crew only.
const kindReservesEquipment = (kind) => kind !== "flight";

const loadFor = (dayType) => (dayType === "partial" ? 0.5 : 1);

/* ═══ Crew identity ═══ */

// The roster id for a Settings roster entry: the lower-cased email when the entry
// has one, else "name:" plus a slug of the name. Stable across display renames
// only when an email exists; that is why the quote stores { id, name } pairs.
export function rosterId(entry) {
  if (!entry) return "";
  if (typeof entry === "string") return entry.trim() ? `name:${slugify(entry)}` : "";
  const email = lower(entry.email);
  if (email) return email;
  const name = clean(entry.name);
  return name ? `name:${slugify(name)}` : "";
}

// The roster indexed once per roster array (lower-cased name to id, first entry
// wins, plus the id set), so per-member lookups never scan the list again.
const rosterIndexCache = new WeakMap();
function rosterIndex(roster) {
  if (!Array.isArray(roster)) return null;
  const cached = rosterIndexCache.get(roster);
  if (cached) return cached;
  const idx = { byName: new Map(), ids: new Set() };
  roster.forEach((r) => {
    const id = rosterId(r);
    if (!id) return;
    idx.ids.add(id);
    const key = lower(typeof r === "string" ? r : r?.name);
    if (key && !idx.byName.has(key)) idx.byName.set(key, id);
  });
  rosterIndexCache.set(roster, idx);
  return idx;
}

// Resolve a job's crew NAME to a roster id: case-insensitive exact name match
// against the live roster, else the name slug, so old jobs still collide with
// quotes that picked the same person.
export function crewIdForName(name, roster) {
  const target = lower(name);
  if (!target) return "";
  const hit = rosterIndex(roster)?.byName.get(target);
  return hit || `name:${slugify(name)}`;
}

// The quote's crew list as stored: [{ id, name }], deduped by id. Strings become
// name-keyed entries. With a roster, a stale id (the person gained an email since
// the pick) is re-resolved by name so the id stays current.
export function normalizeQuoteCrew(list, roster) {
  const out = [];
  const seen = new Set();
  (Array.isArray(list) ? list : normalizeCrew(list)).forEach((c) => {
    if (!c) return;
    const name = clean(typeof c === "string" ? c : c.name);
    let id = typeof c === "string" ? "" : clean(c.id);
    if (roster && roster.length && name) {
      const known = !!id && rosterIndex(roster).ids.has(id);
      if (!known) id = crewIdForName(name, roster);
    }
    if (!id) id = name ? `name:${slugify(name)}` : "";
    if (!id || seen.has(id)) return;
    seen.add(id);
    out.push({ id, name });
  });
  return out;
}

/* ═══ Equipment identity ═══ */

const isShortKey = (k) => k.endsWith("_short");
const baseOfShortKey = (k) => k.slice(0, -"_short".length);

// The chip list: every live unit in the map, in map order, minus the retired
// aliases and the shortName keys. A new id in app/equipment_config appears here
// with no code change.
export function equipmentChipOptions(equipmentMap) {
  const map = equipmentMap || {};
  return Object.keys(map)
    .filter((k) => !isShortKey(k) && !RETIRED_EQUIPMENT_ALIASES[k])
    .map((id) => ({ id, label: clean(map[id]) || id }));
}

export function isUnknownEquipmentId(id) {
  return typeof id === "string" && id.startsWith(UNKNOWN_EQUIPMENT_PREFIX);
}

// Display name for one id: the map's name, the raw string behind an unknown
// token, else the id itself.
export function equipmentLabel(id, equipmentMap) {
  const name = equipmentMap?.[id];
  if (name) return clean(name);
  if (isUnknownEquipmentId(id)) return id.slice(UNKNOWN_EQUIPMENT_PREFIX.length);
  return clean(id);
}

// One lookup per map object (the live map is one long-lived object; a schedule
// computation normalizes hundreds of job rows against it).
const equipmentLookupCache = new WeakMap();
function buildEquipmentLookup(equipmentMap) {
  if (!equipmentMap || typeof equipmentMap !== "object") return null;
  const cached = equipmentLookupCache.get(equipmentMap);
  if (cached) return cached;
  const ids = new Set();
  const byName = new Map();
  Object.entries(equipmentMap || {}).forEach(([key, name]) => {
    if (isShortKey(key)) {
      const base = baseOfShortKey(key);
      if (lower(name) && !byName.has(lower(name))) byName.set(lower(name), RETIRED_EQUIPMENT_ALIASES[base] || base);
      return;
    }
    ids.add(key);
    // A canonical id wins the name over its alias (r5 and r5e both read "Canon R5").
    if (RETIRED_EQUIPMENT_ALIASES[key]) return;
    if (lower(name) && !byName.has(lower(name))) byName.set(lower(name), key);
  });
  // Aliases whose canonical id is missing from the map still resolve by name.
  Object.entries(equipmentMap || {}).forEach(([key, name]) => {
    if (RETIRED_EQUIPMENT_ALIASES[key] && lower(name) && !byName.has(lower(name))) byName.set(lower(name), RETIRED_EQUIPMENT_ALIASES[key]);
  });
  const lookup = { ids, byName };
  equipmentLookupCache.set(equipmentMap, lookup);
  return lookup;
}

// Equipment strings or objects (ids, live names, retired aliases, legacy names,
// any casing or whitespace) to canonical ids, deduped in order. A string nobody
// recognizes becomes "unknown:<lower-cased trimmed name>" so two identical legacy
// strings still collide with each other. Without a map the list is only cleaned,
// never resolved (the pure callers in tests and the save whitelist fallback).
export function normalizeEquipmentIds(list, equipmentMap) {
  const raw = Array.isArray(list) ? list : (list ? [list] : []);
  const lookup = buildEquipmentLookup(equipmentMap);
  const out = [];
  const seen = new Set();
  const push = (id) => { if (id && !seen.has(id)) { seen.add(id); out.push(id); } };
  raw.forEach((item) => {
    let text = "";
    if (typeof item === "string") text = item;
    else if (item && typeof item === "object") text = item.id || item.name || item.label || "";
    text = clean(text);
    if (!text) return;
    const key = text.toLowerCase();
    if (isUnknownEquipmentId(key)) { push(UNKNOWN_EQUIPMENT_PREFIX + clean(key.slice(UNKNOWN_EQUIPMENT_PREFIX.length))); return; }
    if (RETIRED_EQUIPMENT_ALIASES[key]) { push(RETIRED_EQUIPMENT_ALIASES[key]); return; }
    if (!lookup) { push(text); return; }
    if (lookup.ids.has(key)) { push(key); return; }
    if (lookup.byName.has(key)) { push(lookup.byName.get(key)); return; }
    if (LEGACY_EQUIPMENT_NAMES[key]) { push(LEGACY_EQUIPMENT_NAMES[key]); return; }
    push(UNKNOWN_EQUIPMENT_PREFIX + key);
  });
  return out;
}

// The quote's effective equipment ids: the stored list normalized. An array,
// even an empty one, is authoritative (the operator's choice); only a record
// with no equipmentIds field at all reads the legacy display names. migrateQuote
// stamps those names into equipmentIds once at load, so this is the one reader
// the engine, the record, the builder, and the list share.
export function quoteEquipmentIds(quote, equipmentMap) {
  const stored = Array.isArray(quote?.equipmentIds) ? quote.equipmentIds : quoteEquipmentList(quote);
  return normalizeEquipmentIds(stored, equipmentMap);
}

/* ═══ Hold label and dates ═══ */

// Hold number: "Hold " plus the last four characters of the quote id. Never the
// client code; the client NAME rides on the record itself (holdIdentity).
export function holdLabel(quote) {
  const id = clean(typeof quote === "string" ? quote : quote?.id);
  return `Hold ${id.slice(-4)}`;
}

/* ═══ Hold identity (what a hold is called, 2026-09-05) ═══ */

export const HOLD_SUBJECT_MAX = 80;

// Cut `text` to at most `max` characters at a word boundary, then add "…", so a cut
// result is `max` + 1 characters long. Mirrored in functions/src/services/holdRecord.ts.
function truncateAtWord(text, max) {
  if (text.length <= max) return text;
  let head = text.slice(0, max);
  if (!/\s/.test(text.charAt(max))) {
    const at = head.search(/\s+\S*$/);
    if (at > 0) head = head.slice(0, at);
  }
  return `${head.trimEnd()}…`;
}

// The hold's subject, the way a booking's title carries the job description:
// the production name, else the first line of the summary cut to 80 characters
// at a word boundary, else "".
export function holdSubject(quote) {
  const production = clean(quote?.productionName);
  if (production) return truncateAtWord(production, HOLD_SUBJECT_MAX);
  const firstLine = clean(clean(quote?.summary).split(/\r?\n/)[0]);
  return truncateAtWord(firstLine, HOLD_SUBJECT_MAX);
}

// The identity fields a hold record carries beside its dates, gear, and crew:
//   { clientName, subject, contactName, location, isStudio }
// location is the studio address for a studio quote (the constant the builder
// prints), else the quote's jobLocation. contactName is a name only; no phone
// number, no email, no money, no notes ever enter the record.
export function holdIdentity(quote) {
  const isStudio = !!quote?.isStudio;
  return {
    clientName: clean(quote?.clientName),
    subject: holdSubject(quote),
    contactName: clean(quote?.contactName),
    location: isStudio ? STUDIO_ADDRESS : clean(quote?.jobLocation),
    isStudio,
  };
}

// The one title every role sees for a hold, in the booking shape buildEventTitle
// uses ("Brooks Films — Rooftop drone survey (Hold)"): chip and bar tooltips,
// aria labels, and the Google Calendar event summary. The em dash is the
// approved booking format.
export function holdTitle({ clientName, subject } = {}) {
  return `${clean(clientName) || "Client TBD"} — ${clean(subject) || "Untitled"} (Hold)`;
}

// Every date a quote would hold, one row per date, sorted:
//   { date, dayType: "full" | "partial", kinds: ["setup", ...], load: 1 | 0.5 | 0 }
// Multi-day quotes: every dated session. Single-day quotes (no sessions): scanDate
// as one full capture day. TBD, the literal "TBD", and invalid dates are ignored.
// Same-date sessions merge: kinds union, dayType and load from the equipment
// reserving sessions (full beats partial), or from all sessions when only a
// flight day is on that date (load 0: crew only).
export function quoteHoldDates(quote) {
  if (!quote) return [];
  const sessions = Array.isArray(quote.sessions) ? quote.sessions.filter(Boolean) : [];
  const rows = new Map();
  const add = (date, kind, dayType) => {
    if (!isValidHoldDate(date)) return;
    let row = rows.get(date);
    if (!row) { row = { date, kinds: new Set(), eqFull: false, eqAny: false, anyFull: false }; rows.set(date, row); }
    row.kinds.add(kind);
    if (dayType === "full") row.anyFull = true;
    if (kindReservesEquipment(kind)) { row.eqAny = true; if (dayType === "full") row.eqFull = true; }
  };
  if (sessions.length) {
    sessions.forEach((s) => add(s.date, sessionKind(s.sessionType), normalizeDayType(s.dayType)));
  } else if (!quote.scanDateTBD) {
    add(quote.scanDate, "capture", "full");
  }
  return [...rows.values()]
    .sort((a, b) => cmp(a.date, b.date))
    .map((r) => {
      const dayType = r.eqAny ? (r.eqFull ? "full" : "partial") : (r.anyFull ? "full" : "partial");
      return {
        date: r.date,
        dayType,
        kinds: [...r.kinds].sort((a, b) => KIND_ORDER[a] - KIND_ORDER[b]),
        load: r.eqAny ? loadFor(dayType) : 0,
      };
    });
}

// "Oct 7", "Oct 7 and Oct 17 to Oct 28", "Oct 7, Oct 10 to Oct 12, and Oct 20".
// Consecutive dates collapse into a run.
export function describeHoldDates(dates) {
  const list = [...new Set((dates || []).map((d) => (typeof d === "string" ? d : d?.date)).filter(isValidHoldDate))].sort();
  const runs = [];
  list.forEach((d) => {
    const last = runs[runs.length - 1];
    if (last && shiftDay(last.end, 1) === d) last.end = d;
    else runs.push({ start: d, end: d });
  });
  const parts = runs.map((r) => (r.start === r.end ? fmtShort(r.start) : `${fmtShort(r.start)} to ${fmtShort(r.end)}`));
  if (parts.length <= 1) return parts[0] || "";
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(", ")}, and ${parts[parts.length - 1]}`;
}

/* ═══ Lifecycle ═══ */

// A quote holds while it is open: jobStatus "quoted", not a draft, not in the
// trash, the switch on, and at least one dated session. Booked (a live job with
// this quoteId, or the status), lost, archived, completed: no hold. No expiry.
export function isHoldingQuote(quote, { hasLiveJob = false } = {}) {
  if (!quote) return false;
  if (quote.draft) return false;
  if (quote.deletedAt) return false;
  if (quote.holdOff) return false;
  if (hasLiveJob) return false;
  if (lower(quote.jobStatus || "quoted") !== "quoted") return false;
  return quoteHoldDates(quote).length > 0;
}

// Admin badge only: the quote has not been touched for 30 days. Reads the last
// write stamp (updatedAt, then savedAt), falling back to quoteDate.
export function isStaleQuote(quote, now = new Date()) {
  const stamp = quote?.updatedAt || quote?.savedAt || quote?.quoteDate || "";
  if (!stamp) return false;
  const t = isISODateOnly(stamp) ? new Date(`${stamp}T00:00:00`).getTime() : new Date(stamp).getTime();
  if (Number.isNaN(t)) return false;
  const nowMs = now instanceof Date ? now.getTime() : new Date(now).getTime();
  return nowMs - t > STALE_HOLD_DAYS * DAY_MS;
}

/* ═══ The hold record ═══ */

// The record's fingerprint: a canonical JSON serialization of everything the
// record shows (the dates with day type and kinds, the unit ids sorted, the
// crew as { id, name } pairs sorted by id, and the identity fields). Selection
// order never changes it; a crew rename, an equipment change, a client rename,
// or a new subject does, so the projection rewrites the record and the calendar
// events follow.
export function holdFingerprint(dates, equipmentIds, crew, identity = {}) {
  return canonicalJson({
    dates: (dates || []).map(({ date, dayType, kinds }) => ({ date, dayType, kinds: [...(kinds || [])] })),
    equipmentIds: [...(equipmentIds || [])].sort(),
    crew: (crew || []).map(({ id, name }) => ({ id, name })).sort((a, b) => cmp(a.id, b.id)),
    clientName: clean(identity?.clientName),
    subject: clean(identity?.subject),
    contactName: clean(identity?.contactName),
    location: clean(identity?.location),
    isStudio: !!identity?.isStudio,
  });
}

// Exactly the holds/{quoteId} document, or null when the quote does not hold:
// the label, the identity fields (client name, subject, contact name, location,
// studio flag), the dates, the unit ids, the crew, and the fingerprint. No
// client code, no money, no notes, no phone numbers.
export function deriveHoldRecord(quote, { hasLiveJob = false, equipmentMap = null } = {}) {
  if (!isHoldingQuote(quote, { hasLiveJob })) return null;
  const identity = holdIdentity(quote);
  const dates = quoteHoldDates(quote).map(({ date, dayType, kinds }) => ({ date, dayType, kinds }));
  const equipmentIds = quoteEquipmentIds(quote, equipmentMap);
  const crew = normalizeQuoteCrew(quote.crew).map(({ id, name }) => ({ id, name }));
  return { label: holdLabel(quote), ...identity, dates, equipmentIds, crew, fingerprint: holdFingerprint(dates, equipmentIds, crew, identity) };
}

/* ═══ Entries (the rows the conflict check reads) ═══ */

// Entry: { key, owner, kind, label, clientName, date, dayType, kinds,
//          equipmentIds, loads: { [equipmentId]: 1 | 0.5 }, crewIds, crewNames }
// A hold entry also carries subject, contactName, and location from the record.
// key is unique per row; owner groups one quote's or one job's rows so a job's
// setup and capture on the same day never stack against each other.
// equipmentIds is the owner's full list for display; loads is what the date
// actually reserves (empty on a flight day).

function holdEntriesForQuote(quote, roster, equipmentMap) {
  const label = holdLabel(quote);
  const { clientName, subject, contactName, location } = holdIdentity(quote);
  const equipmentIds = quoteEquipmentIds(quote, equipmentMap);
  const crew = normalizeQuoteCrew(quote.crew, roster);
  return quoteHoldDates(quote).map((row) => ({
    key: `hold:${quote.id}:${row.date}`,
    owner: `hold:${quote.id}`,
    kind: "hold",
    label,
    clientName,
    subject,
    contactName,
    location,
    date: row.date,
    dayType: row.dayType,
    kinds: row.kinds,
    equipmentIds,
    loads: row.load > 0 ? Object.fromEntries(equipmentIds.map((id) => [id, row.load])) : {},
    crewIds: crew.map((c) => c.id),
    crewNames: crew.map((c) => c.name),
  }));
}

const liveJobsOf = (jobs) => (Array.isArray(jobs) ? jobs.filter((j) => j && !j.deletedAt) : []);

// Entries for every holding quote.
export function holdEntries(quotes, jobs, roster, { equipmentMap = null } = {}) {
  const live = liveJobsOf(jobs);
  const out = [];
  (Array.isArray(quotes) ? quotes : []).forEach((q) => {
    if (!q) return;
    const hasLiveJob = live.some((j) => j.quoteId === q.id);
    if (!isHoldingQuote(q, { hasLiveJob })) return;
    out.push(...holdEntriesForQuote(q, roster, equipmentMap));
  });
  return out;
}

// The job code as crew already see it on call sheets: the first segment of the
// jobId ("atlpow_261007" reads "atlpow"), else the client code, else the id.
export function jobLabel(job) {
  const code = clean(job?.jobId).split("_")[0];
  return code || clean(job?.clientCode) || clean(job?.id);
}

// Entries for every non-deleted job: each dated session (captureDate as one full
// capture day when there are no sessions), equipment from the job's display names
// normalized to ids, crew names matched to roster ids.
export function jobEntries(jobs, roster, { equipmentMap = null } = {}) {
  const out = [];
  liveJobsOf(jobs).forEach((job) => {
    const equipmentIds = normalizeEquipmentIds(quoteEquipmentList(job), equipmentMap);
    const crewNames = normalizeCrew(job.crew);
    const crewIds = crewNames.map((n) => crewIdForName(n, roster));
    const sessions = Array.isArray(job.sessions) && job.sessions.length
      ? job.sessions
      : (job.captureDate ? [{ id: "captureDate", sessionType: "capture", date: job.captureDate, dayType: "full" }] : []);
    sessions.forEach((s, i) => {
      if (!s || !isValidHoldDate(s.date)) return;
      const kind = sessionKind(s.sessionType);
      const dayType = normalizeDayType(s.dayType);
      const load = kindReservesEquipment(kind) ? loadFor(dayType) : 0;
      out.push({
        key: `job:${job.id}:${s.id || i}`,
        owner: `job:${job.id}`,
        kind: "job",
        label: jobLabel(job),
        clientName: clean(job.clientName),
        date: s.date,
        dayType,
        kinds: [kind],
        equipmentIds,
        loads: load > 0 ? Object.fromEntries(equipmentIds.map((id) => [id, load])) : {},
        crewIds,
        crewNames,
      });
    });
  });
  return out;
}

/* ═══ Entries from hold RECORDS (the calendars' source) ═══ */

// The strip and the Schedule view read holds/{quoteId} records plus jobs, so
// admin and crew see one schedule with the same names on it. A record row
// becomes the same entry shape holdEntriesForQuote produces: kind "hold", key
// "hold:<id>:<date>", owner "hold:<id>", label, client name, subject, contact
// name, and location from the record. Loads follow the kinds already encoded in
// the record (a flight-only date reserves crew only). `quotes` is a fallback
// for records written before 2026-09-05, which carry no clientName field: such
// an entry takes the client name from the matching quote when the caller has
// one (admins), else prints without it until the backfill rewrites the record.
export function holdRecordEntries(holds, roster, { equipmentMap = null, quotes = null } = {}) {
  const clientById = new Map();
  (Array.isArray(quotes) ? quotes : []).forEach((q) => { if (q?.id) clientById.set(q.id, clean(q.clientName)); });
  const out = [];
  (Array.isArray(holds) ? holds : []).forEach((rec) => {
    const id = clean(rec?.id || rec?.quoteId);
    if (!id) return;
    const label = clean(rec.label) || holdLabel(id);
    const equipmentIds = normalizeEquipmentIds(rec.equipmentIds, equipmentMap);
    const crew = normalizeQuoteCrew(rec.crew, roster);
    const clientName = rec.clientName === undefined ? (clientById.get(id) || "") : clean(rec.clientName);
    const subject = clean(rec.subject);
    const contactName = clean(rec.contactName);
    const location = clean(rec.location);
    const seen = new Set();
    (Array.isArray(rec.dates) ? rec.dates : []).forEach((row) => {
      const date = typeof row === "string" ? row : row?.date;
      if (!isValidHoldDate(date) || seen.has(date)) return;
      seen.add(date);
      const dayType = normalizeDayType(row?.dayType);
      const rawKinds = Array.isArray(row?.kinds) && row.kinds.length ? row.kinds : ["capture"];
      const kinds = [...new Set(rawKinds.map(sessionKind))].sort((a, b) => KIND_ORDER[a] - KIND_ORDER[b]);
      const reserves = kinds.some(kindReservesEquipment);
      out.push({
        key: `hold:${id}:${date}`,
        owner: `hold:${id}`,
        kind: "hold",
        label,
        clientName,
        subject,
        contactName,
        location,
        date,
        dayType,
        kinds,
        equipmentIds,
        loads: reserves ? Object.fromEntries(equipmentIds.map((eid) => [eid, loadFor(dayType)])) : {},
        crewIds: crew.map((c) => c.id),
        crewNames: crew.map((c) => c.name),
      });
    });
  });
  return out.sort((a, b) => cmp(a.date, b.date) || cmp(a.key, b.key));
}

/* ═══ Conflicts ═══ */

// Per date, per equipment unit: the loads of every owner using it (one quote's
// or one job's rows count once, at their max) sum over 1 and every user is marked.
// Per person: two or more owners on one date mark each other. Warn, never block.
// Returns Map<entryKey, { key, date, reasons }>, reasons being
//   { type: "equipment", id, date, load, otherKey, otherLoad } or
//   { type: "crew", id, name, date, otherKey }
// with one reason per other owner (a job's two same-day sessions are one reason,
// and otherLoad is that owner's effective load on the date, its max across rows).
export function findConflicts(entries) {
  const byDate = new Map();
  (Array.isArray(entries) ? entries : []).forEach((e) => {
    if (!e || !isValidHoldDate(e.date)) return;
    if (!byDate.has(e.date)) byDate.set(e.date, []);
    byDate.get(e.date).push(e);
  });
  const out = new Map();
  const add = (entry, reason, otherOwner) => {
    let rec = out.get(entry.key);
    if (!rec) { rec = { key: entry.key, date: entry.date, reasons: [], _seen: new Set() }; out.set(entry.key, rec); }
    const sig = `${reason.type}|${reason.id}|${otherOwner}`;
    if (rec._seen.has(sig)) return;
    rec._seen.add(sig);
    rec.reasons.push(reason);
  };
  for (const [date, list] of byDate) {
    const units = new Map();
    list.forEach((e) => Object.entries(e.loads || {}).forEach(([id, load]) => {
      if (!(load > 0)) return;
      if (!units.has(id)) units.set(id, []);
      units.get(id).push(e);
    }));
    for (const [id, users] of units) {
      const ownerLoad = new Map();
      users.forEach((e) => ownerLoad.set(e.owner, Math.max(ownerLoad.get(e.owner) || 0, e.loads[id])));
      const total = [...ownerLoad.values()].reduce((a, b) => a + b, 0);
      if (total <= 1 + 1e-9) continue;
      users.forEach((e) => users.forEach((o) => {
        if (o.owner === e.owner) return;
        add(e, { type: "equipment", id, date, load: e.loads[id], otherKey: o.key, otherLoad: ownerLoad.get(o.owner) }, o.owner);
      }));
    }
    const people = new Map();
    list.forEach((e) => (e.crewIds || []).forEach((cid, i) => {
      if (!cid) return;
      if (!people.has(cid)) people.set(cid, []);
      people.get(cid).push({ entry: e, name: clean(e.crewNames?.[i]) || cid });
    }));
    for (const [cid, users] of people) {
      if (new Set(users.map((u) => u.entry.owner)).size < 2) continue;
      users.forEach((u) => users.forEach((o) => {
        if (o.entry.owner === u.entry.owner) return;
        add(u.entry, { type: "crew", id: cid, name: u.name, date, otherKey: o.entry.key }, o.entry.owner);
      }));
    }
  }
  for (const rec of out.values()) delete rec._seen;
  return out;
}

// Everything at once for a list screen: the entries by key and the conflicts.
export function scheduleConflicts(quotes, jobs, roster, opts = {}) {
  const entries = [...holdEntries(quotes, jobs, roster, opts), ...jobEntries(jobs, roster, opts)];
  const byKey = new Map(entries.map((e) => [e.key, e]));
  return { entries, byKey, conflicts: findConflicts(entries) };
}

// The schedule surfaces' one call: entries from records plus jobs, by key, and
// the conflicts between them. Same shape as scheduleConflicts.
export function scheduleEntries(holds, jobs, roster, opts = {}) {
  const { equipmentMap = null, quotes = null } = opts;
  const entries = [...holdRecordEntries(holds, roster, { equipmentMap, quotes }), ...jobEntries(jobs, roster, { equipmentMap })];
  const byKey = new Map(entries.map((e) => [e.key, e]));
  return { entries, byKey, conflicts: findConflicts(entries) };
}

const attachOthers = (rec, byKey) => ({
  key: rec.key,
  date: rec.date,
  reasons: rec.reasons.map((r) => ({ ...r, other: byKey.get(r.otherKey) || null })),
});

// The conflict rows for one quote out of a scheduleConflicts() result, sorted by date.
export function conflictsForQuote(quoteId, { byKey, conflicts }) {
  const owner = `hold:${quoteId}`;
  return [...conflicts.values()]
    .filter((rec) => byKey.get(rec.key)?.owner === owner)
    .sort((a, b) => cmp(a.date, b.date))
    .map((rec) => attachOthers(rec, byKey));
}

// Conflicts touching the quote being edited, computed on the in-memory quote as
// if it were holding (the draft flag and trash stamp are ignored so an unsaved or
// draft quote previews its collisions), empty when the switch is off, when the
// quote is not open, or when it has no dates. The quote's own saved copy and its
// own live job are excluded so it never collides with itself.
export function quoteConflicts(quote, quotes, jobs, roster, { equipmentMap = null } = {}) {
  if (!quote || quote.holdOff) return [];
  const live = liveJobsOf(jobs);
  const hasLiveJob = live.some((j) => j.quoteId === quote.id);
  const probe = { ...quote, draft: false, deletedAt: null };
  if (!isHoldingQuote(probe, { hasLiveJob })) return [];
  const others = (Array.isArray(quotes) ? quotes : []).filter((sq) => sq && sq.id !== quote.id);
  const entries = [
    ...holdEntries(others, live, roster, { equipmentMap }),
    ...holdEntriesForQuote(probe, roster, equipmentMap),
    ...jobEntries(live.filter((j) => j.quoteId !== quote.id), roster, { equipmentMap }),
  ];
  const byKey = new Map(entries.map((e) => [e.key, e]));
  return conflictsForQuote(quote.id, { byKey, conflicts: findConflicts(entries) });
}

/* ═══ Sentences ═══ */

const lookupEntry = (reason, entriesByKey) => {
  if (reason?.other) return reason.other;
  if (!entriesByKey) return null;
  if (typeof entriesByKey.get === "function") return entriesByKey.get(reason.otherKey) || null;
  if (Array.isArray(entriesByKey)) return entriesByKey.find((e) => e?.key === reason.otherKey) || null;
  return null;
};

// One complete sentence per reason, the same for every role:
//   "Conflict on Oct 7: the Drone is also held by Hold 4812 (Brooks Films, partial day)."
//   "Conflict on Oct 7: the Drone is booked by job atlpow (Atlantic Power Solutions)."
//   "Conflict on Oct 7: Peter is on Hold 4812."
// `anonymized` drops the client names. Kept for compatibility; since 2026-09-05
// no surface passes it (crew see the client on holds as they do on bookings).
export function conflictSentence(reason, entriesByKey, { anonymized = false, equipmentMap = null } = {}) {
  if (!reason) return "";
  const other = lookupEntry(reason, entriesByKey);
  const when = fmtShort(reason.date);
  const client = !anonymized && other && clean(other.clientName) ? clean(other.clientName) : "";
  if (reason.type === "equipment") {
    const unit = equipmentLabel(reason.id, equipmentMap);
    if (!other) return `Conflict on ${when}: the ${unit} is also in use.`;
    if (other.kind === "job") {
      return client
        ? `Conflict on ${when}: the ${unit} is booked by job ${other.label} (${client}).`
        : `Conflict on ${when}: the ${unit} is booked by job ${other.label}.`;
    }
    const otherLoad = reason.otherLoad ?? other.loads?.[reason.id];
    const day = otherLoad === 0.5 ? "partial day" : "full day";
    return client
      ? `Conflict on ${when}: the ${unit} is also held by ${other.label} (${client}, ${day}).`
      : `Conflict on ${when}: the ${unit} is also held by ${other.label} (${day}).`;
  }
  const who = clean(reason.name) || reason.id;
  if (!other) return `Conflict on ${when}: ${who} is on another entry.`;
  if (other.kind === "job") {
    return client
      ? `Conflict on ${when}: ${who} is on job ${other.label} (${client}).`
      : `Conflict on ${when}: ${who} is on job ${other.label}.`;
  }
  return `Conflict on ${when}: ${who} is on ${other.label}.`;
}

// A Map (or anything with .get) or an array of entries; a plain options object
// is not one, so the two-argument form conflictSentences(rows, opts) still works.
const looksLikeEntries = (v) => !!v && (typeof v.get === "function" || Array.isArray(v));

// All sentences for a quote's conflict rows, in date order, deduped. With
// `entriesByKey` (the byKey Map or an entry array), a reason whose `other` is
// not hydrated still names the other hold or job; without it, and without
// `other`, the sentence falls back to "is also in use".
export function conflictSentences(rows, entriesByKey = null, opts = {}) {
  if (!looksLikeEntries(entriesByKey)) {
    if (entriesByKey && typeof entriesByKey === "object") opts = {...entriesByKey, ...(opts || {})};
    entriesByKey = null;
  }
  const out = [];
  const seen = new Set();
  (rows || []).forEach((row) => (row.reasons || []).forEach((r) => {
    const s = conflictSentence({ ...r, date: r.date || row.date }, entriesByKey, opts);
    if (s && !seen.has(s)) { seen.add(s); out.push(s); }
  }));
  return out;
}
